import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const Results = () => {
  const { sessionId } = useParams();
  const [results, setResults] = useState(null);
  const [comparisonResults, setComparisonResults] = useState(null);
  const [isSecondSession, setIsSecondSession] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadResults();
    const interval = setInterval(loadResults, 3000); // Poll every 3 seconds
    return () => clearInterval(interval);
  }, [sessionId]);

  const loadResults = async () => {
    try {
      // Get all sessions to determine if this is session 2
      const sessionsResponse = await axios.get('/api/sessions');
      const sessions = sessionsResponse.data;

      // Get current session results
      const response = await axios.get(`/api/sessions/${sessionId}/results`);
      setResults(response.data);

      // If there are 2 sessions and this is the second one (by creation time), load comparison
      if (sessions.length === 2) {
        // Sort by created_at (oldest first)
        const sortedSessions = [...sessions].sort((a, b) =>
          new Date(a.created_at) - new Date(b.created_at)
        );

        const firstSessionId = sortedSessions[0].id;
        const secondSessionId = sortedSessions[1].id;

        if (sessionId === secondSessionId) {
          // This is session 2, load session 1 for comparison
          setIsSecondSession(true);
          const comparisonResponse = await axios.get(`/api/sessions/${firstSessionId}/results`);
          setComparisonResults(comparisonResponse.data);
        } else {
          setIsSecondSession(false);
          setComparisonResults(null);
        }
      } else {
        setIsSecondSession(false);
        setComparisonResults(null);
      }

      setError(null);
    } catch (error) {
      console.error('Error loading results:', error);
      setError('Error loading results');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <h2>Loading Results...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="card">
        <h2>No Results Available</h2>
      </div>
    );
  }

  // Helper function to calculate deltas
  const calculateDelta = (session1Result, session2Result) => {
    const voteDelta = session2Result.votes - session1Result.votes;
    const percentageDelta = session2Result.percentage - session1Result.percentage;
    return { voteDelta, percentageDelta };
  };

  // Render comparison view if this is session 2
  if (isSecondSession && comparisonResults) {
    const session1Question = comparisonResults.questions[0];
    const session2Question = results.questions[0];

    return (
      <div className="container">
        <h2 style={{ color: 'var(--white)', marginBottom: '2rem', textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)' }}>
          Live Results: {results.session.title}
        </h2>

        <div className="comparison-container">
          {/* Session 2 Current Results - Standalone */}
          <div className="card">
            <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>Current Results</h3>
            <div className="results-container">
              <div className="question-results">
                <h3>{session2Question.questionText}</h3>
                <p><strong>Total Votes: {session2Question.totalVotes}</strong></p>

                <div className="vertical-results">
                  {session2Question.results.map((result, idx) => (
                    <div key={idx} className="vertical-result-item">
                      <div className="result-label">
                        <span>{result.option}</span>
                        <span>{result.votes} votes ({result.percentage}%)</span>
                      </div>
                      <div className="vertical-result-bar-container">
                        <div
                          className="vertical-result-bar"
                          style={{ height: `${result.percentage}%` }}
                        >
                          <span className="bar-percentage">{result.percentage}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Vertical Bar Chart Comparison */}
          <div className="card">
            <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>Side-by-Side Bar Comparison</h3>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', height: '400px', borderBottom: '2px solid #ddd', paddingBottom: '10px' }}>
                {session1Question.results.map((session1Result, idx) => {
                  const session2Result = session2Question.results[idx] || { votes: 0, percentage: 0, option: '' };
                  const maxHeight = 350;

                  return (
                    <div key={idx} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexDirection: 'column' }}>
                      <div style={{ textAlign: 'center', marginBottom: '10px', minHeight: '40px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', maxWidth: '140px', wordWrap: 'break-word' }}>
                          {session1Result.option}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                        {/* Before bar */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <div style={{
                            width: '50px',
                            height: `${(session1Result.percentage / 100) * maxHeight}px`,
                            background: '#888',
                            borderRadius: '4px 4px 0 0',
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'center',
                            paddingBottom: '5px',
                            minHeight: '30px'
                          }}>
                            <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>
                              {session1Result.percentage}%
                            </span>
                          </div>
                          <div style={{ fontSize: '10px', color: '#666', marginTop: '5px' }}>Before</div>
                        </div>
                        {/* After bar */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <div style={{
                            width: '50px',
                            height: `${(session2Result.percentage / 100) * maxHeight}px`,
                            background: 'var(--primary-color)',
                            borderRadius: '4px 4px 0 0',
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'center',
                            paddingBottom: '5px',
                            minHeight: '30px'
                          }}>
                            <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>
                              {session2Result.percentage}%
                            </span>
                          </div>
                          <div style={{ fontSize: '10px', color: '#666', marginTop: '5px' }}>After</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Percentage Change Visualization */}
          <div className="card">
            <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>Vote Distribution Change</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {session2Question.results.map((session2Result, idx) => {
                const session1Result = session1Question.results[idx] || { votes: 0, percentage: 0, option: '' };
                const { percentageDelta } = calculateDelta(session1Result, session2Result);

                return (
                  <div key={idx}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span><strong>{session2Result.option}</strong></span>
                      <span style={{
                        color: percentageDelta >= 0 ? '#28a745' : '#dc3545',
                        fontWeight: 'bold'
                      }}>
                        {percentageDelta >= 0 ? '↑' : '↓'} {Math.abs(percentageDelta)}%
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      {/* Before bar */}
                      <div style={{ flex: 1 }}>
                        <div style={{
                          height: '30px',
                          background: '#ddd',
                          borderRadius: '4px',
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${session1Result.percentage}%`,
                            height: '100%',
                            background: '#888',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {session1Result.percentage}%
                          </div>
                        </div>
                      </div>
                      <div style={{ width: '30px', textAlign: 'center', fontSize: '20px' }}>
                        →
                      </div>
                      {/* After bar */}
                      <div style={{ flex: 1 }}>
                        <div style={{
                          height: '30px',
                          background: '#ddd',
                          borderRadius: '4px',
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${session2Result.percentage}%`,
                            height: '100%',
                            background: 'var(--primary-color)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {session2Result.percentage}%
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Regular results view (for session 1 or when only 1 session exists)
  return (
    <div className="container">
      <h2 style={{ color: 'var(--white)', marginBottom: '2rem', textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)' }}>Live Results: {results.session.title}</h2>

      <div className="results-container">
        {results.questions.map((question, index) => (
          <div key={question.questionId} className="question-results">
            <h3>{index + 1}. {question.questionText}</h3>
            <p><strong>Total Votes: {question.totalVotes}</strong></p>

            <div className="vertical-results">
              {question.results.map((result, idx) => (
                <div key={idx} className="vertical-result-item">
                  <div className="result-label">
                    <span>{result.option}</span>
                    <span>{result.votes} votes ({result.percentage}%)</span>
                  </div>
                  <div className="vertical-result-bar-container">
                    <div
                      className="vertical-result-bar"
                      style={{ height: `${result.percentage}%` }}
                    >
                      <span className="bar-percentage">{result.percentage}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Results;